import {DataStatus} from 'platform/components';
import {Grid, Show, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {usePermissions} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {DetailsSideColumn} from '../DetailsSideColumn/DetailsSideColumn';
import {ConditionsCard} from './components/ConditionsCard';
import {ServiceBookCard} from './components/ServiceBookCard';
import {ServiceConditionsCard} from './components/ServiceConditionsCard';
import {ServiceInspectionsCard} from './components/ServiceInspectionsCard';
import {TechnicalInspectionCard} from './components/TechnicalInspectionCard';
import {WarrantyCard} from './components/WarrantyCard';

interface VehicleDataProps extends TestIdProps {
  isSaleVehicle: boolean;
  vehicleId: string;
}

export function VehicleData(props: VehicleDataProps) {
  const {
    data: vehicleDetail,
    isLoading,
    isError,
  } = useGetVehicleQuery({vehicleId: props.vehicleId});
  const {data: vehicleParticipation} = useGetParticipationQuery({
    recordId: props.vehicleId,
    resourceId: EntityResourceIds.vehicle,
  });

  const [canUpdateVehicle, canSetCustomVehicleId, canReadAdvertisingSettings, canReadServiceCase] =
    usePermissions({
      permissionKeys: [
        'updateVehicle',
        'setCustomIdVehicle',
        'readAdvertisingSettings',
        'serviceCaseRead',
      ],
      scopes: {
        updateVehicle: {
          participation: vehicleParticipation,
          branchId: vehicleDetail?.branchId,
        },
        setCustomIdVehicle: {
          participation: vehicleParticipation,
          branchId: vehicleDetail?.branchId,
        },
        readAdvertisingSettings: {
          participation: vehicleParticipation,
          branchId: vehicleDetail?.branchId,
        },
      },
    });
  const canEditVehicle = canUpdateVehicle && canSetCustomVehicleId;

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Show when={isNotNil(vehicleDetail)}>
        <Grid columns={[1, 2]}>
          <VStack spacing={4}>
            <Show when={!props.isSaleVehicle && canReadServiceCase}>
              <ServiceConditionsCard canEditVehicle={canEditVehicle} vehicleId={props.vehicleId} />
              <WarrantyCard canEditVehicle={canEditVehicle} vehicleId={props.vehicleId} />
              <TechnicalInspectionCard
                canEditVehicle={canEditVehicle}
                vehicleId={props.vehicleId}
              />
              <ServiceBookCard
                canEditVehicle={canEditVehicle}
                vehicle={vehicleDetail}
                state={vehicleDetail?.state || {}}
              />
              <ServiceInspectionsCard canEditVehicle={canEditVehicle} vehicleId={props.vehicleId} />
            </Show>
            <Show when={props.isSaleVehicle || (!props.isSaleVehicle && !canReadServiceCase)}>
              <ConditionsCard
                canEditVehicle={canEditVehicle}
                canReadAdvertisingSettings={canReadAdvertisingSettings}
                id={props.vehicleId}
                type={vehicleDetail?.type}
                state={vehicleDetail?.state || {}}
                modelSpecification={vehicleDetail?.modelSpecification || {}}
                vehicle={vehicleDetail?.vehicle || {}}
                vehicleSourceCodeId={vehicleDetail?.vehicleSourceCodeId}
              />
              <WarrantyCard canEditVehicle={canEditVehicle} vehicleId={props.vehicleId} />
              <TechnicalInspectionCard
                canEditVehicle={canEditVehicle}
                vehicleId={props.vehicleId}
              />
              <ServiceBookCard
                canEditVehicle={canEditVehicle}
                vehicle={vehicleDetail}
                state={vehicleDetail?.state || {}}
              />
              <ServiceInspectionsCard canEditVehicle={canEditVehicle} vehicleId={props.vehicleId} />
            </Show>
          </VStack>
          <VStack spacing={4}>
            <DetailsSideColumn
              vehicleId={props.vehicleId}
              isInsuranceShown
              data-testid={suffixTestId('detailsSideColumn', props)}
            />
          </VStack>
        </Grid>
      </Show>
    </DataStatus>
  );
}
